/**
 * TimeRail (docs/11 §3.3, extended to a week): everything in range in exit
 * order, grouped by day — Tonight, Tomorrow, then "Thursday, Nov 6" — with
 * the map as a labelled toggle that REPLACES the rail (never stacked above
 * it). The map shows every pin in range, including the Next up event, and
 * the toggle is hidden only when there is nothing at all to pin.
 */
import { Suspense, lazy, useState } from 'react';
import { Map as MapIcon, List } from 'lucide-react';
import type { Opp } from './api.js';
import { EventRow } from './EventCard.js';
import { dayGroup } from './format.js';

const MapView = lazy(() => import('./MapView.js'));

export function TimeRail({ rows, pins, now, units, hasOrigin, center, radiusMi, origin, footer, onOpen }: {
  /** Rail rows (everything except the Next up card's event). */
  rows: Opp[];
  /** Everything in range, for the map. */
  pins: Opp[];
  now: number;
  units: 'mi' | 'km';
  hasOrigin: boolean;
  center: { lat: number; lng: number } | null;
  radiusMi: number;
  origin: { lat: number; lng: number } | null;
  footer: React.ReactNode;
  onOpen: (opp: Opp) => void;
}) {
  const [mode, setMode] = useState<'list' | 'map'>('list');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const groups: { key: string; label: string; rows: Opp[] }[] = [];
  for (const o of rows) {
    const g = dayGroup(o.egress.p50_at, now);
    const last = groups[groups.length - 1];
    if (last && last.key === g.key) last.rows.push(o);
    else groups.push({ ...g, rows: [o] });
  }
  const canMap = pins.length > 0 && center !== null;
  const selected = pins.find((o) => o.decision_candidate_id === selectedId) ?? null;
  const firstLabel = groups[0]?.label ?? (pins.length > 0 ? 'This week' : 'Tonight');

  function pickPin(opp: Opp) {
    if (selectedId === opp.decision_candidate_id) { onOpen(opp); return; }
    setSelectedId(opp.decision_candidate_id);
  }

  return (
    <>
      <div className="rail-head">
        <h2 className="heading">{mode === 'map' ? 'On the map' : firstLabel}</h2>
        {canMap && (
          <button className="btn secondary map-toggle" aria-pressed={mode === 'map'}
            onClick={() => setMode(mode === 'map' ? 'list' : 'map')}>
            {mode === 'map'
              ? <><List size={20} aria-hidden="true" /> List</>
              : <><MapIcon size={20} aria-hidden="true" /> Map</>}
          </button>
        )}
      </div>

      {mode === 'map' && canMap ? (
        <Suspense fallback={<div className="map-wrap map-loading">Loading map…</div>}>
          <div className="map-wrap">
            <MapView center={center!} radiusMi={radiusMi} origin={origin} opps={pins}
              selectedId={selectedId} onPick={pickPin} />
            {selected && (
              <div className="map-card">
                <EventRow opp={selected} now={now} units={units} hasOrigin={hasOrigin}
                  selected onOpen={() => onOpen(selected)} />
              </div>
            )}
          </div>
        </Suspense>
      ) : (
        <div className="rail">
          {groups.map((g, i) => (
            <div key={g.key} className="rail-group">
              {i > 0 && <h3 className="heading rail-sub">{g.label}</h3>}
              {g.rows.map((o) => (
                <EventRow key={o.decision_candidate_id} opp={o} now={now} units={units}
                  hasOrigin={hasOrigin} hideDay onOpen={() => onOpen(o)} />
              ))}
            </div>
          ))}
        </div>
      )}
      {footer}
    </>
  );
}
